/**
 * Browser-side migration pipeline (Phase 5 cutover).
 *
 * End-to-end pipeline that runs entirely client-side:
 *   1. Parse + validate the uploaded XML in a Web Worker.
 *   2. Map the parsed configuration to Velocity components (main thread).
 *   3. Generate the Velocity JSON (main thread).
 *   4. Build the schema-validated `MigrationReportModel`.
 *
 * Mapping and generation run on the main thread (rather than in the
 * worker) because both already produce sub-second results on realistic
 * inputs and the synchronous APIs are simpler to wire than another
 * round-trip. The worker still owns the heavier parse + probe work.
 *
 * The functional matrix is bundled into the wizard via a static JSON
 * import so the pipeline has no network dependency once the wizard is
 * loaded. The matrix is parsed and cached on first use.
 */

import type { TargetPlatform } from '../../types/velocity.js';
import type { MigrationStrategy } from '../../types/api.js';
import type { MigrationReportModel } from '../../types/render-model.js';
import type { MigrationDocument } from '../../types/generator.js';
import type { GeoEventConfiguration } from '../../types/geoevent.js';
import type { ValidationResult } from '../../types/parser.js';
import type { ConfigurationMappingResult } from '../../types/mapping.js';
import type { VelocityConfiguration } from '../../types/velocity.js';
import {
  buildFunctionalMatrix,
  type LoadedFunctionalMatrix,
} from '../../mapper/matrix-loader.js';
import { mapConfiguration } from '../../mapper/component-mapper.js';
import { generateVelocityJson, serializeConfiguration } from '../../generator/json-generator.js';
import { buildMigrationDocument, buildRenderModelFromDocument } from '../../docs/document-builder.js';
import { renderConfiguration } from '../../renderer/mermaid-renderer.js';
import { summarize, buildWarningRows } from './render-model-builder.js';
import { attachProbeResultsToDocument } from './probe-attach.js';
import { migrationReportModelSchema } from '../../types/render-model.js';
import { runParsePipeline, type OrchestratorOptions, type OrchestratorProgressEvent } from './orchestrator.js';
import type { ProbeTargetResult } from './probe/orchestrator.js';

// Matrix is bundled via a static JSON import so there is no extra HTTP
// request and no race window between parse and map. esbuild inlines
// the file as a JS object.
import functionalMatrixRaw from '../../mapper/functional-matrix.json';

let cachedMatrix: LoadedFunctionalMatrix | null = null;
function getBundledMatrix(): LoadedFunctionalMatrix {
  if (!cachedMatrix) {
    cachedMatrix = buildFunctionalMatrix(functionalMatrixRaw);
  }
  return cachedMatrix;
}

export interface RunMigrationPipelineInput {
  file: File;
  targetPlatform: TargetPlatform;
  migrationStrategy: MigrationStrategy;
  /**
   * When true (default), nested Group fields and Many-cardinality arrays are
   * flattened at the Velocity feed level and the Map Fields tool references the
   * resulting flattened scalar field names. When false, the feed is left
   * un-flattened to mirror the GeoEvent configuration.
   */
  flattenNestedSchema?: boolean;
  onProgress?: (event: OrchestratorProgressEvent) => void;
  orchestratorOptions?: OrchestratorOptions;
}

export interface RunMigrationPipelineResult {
  config: GeoEventConfiguration;
  validation: ValidationResult;
  mappingResult: ConfigurationMappingResult;
  velocityConfig: VelocityConfiguration | null;
  /** Serialized velocity.json string ready for blob/download. */
  velocityJsonText: string;
  hasVelocityJson: boolean;
  renderModel: MigrationReportModel;
  /**
   * The full rich `MigrationDocument` produced client-side. The HTML
   * renderer consumes this directly so it can include flow-diagram
   * Mermaid syntax as a `<pre>` fallback that the render-model cannot
   * carry (the model requires PNG dataUrl assets the browser cannot
   * produce). PDF/DOCX are produced server-side from `renderModel`.
   */
  migrationDocument: MigrationDocument;
  probeResults: ProbeTargetResult[];
  sourceFilenameDisplay: string;
  warnings: string[];
  errors: string[];
}

/**
 * Runs the full browser-side migration pipeline. The XML never leaves
 * the user's browser; PDF/DOCX rendering remains server-side via the
 * stateless `POST /api/render` endpoint, fed only the validated
 * `MigrationReportModel` (no XML, no parsed config, no credentials).
 */
export async function runMigrationPipeline(
  input: RunMigrationPipelineInput,
): Promise<RunMigrationPipelineResult> {
  const onProgress = input.onProgress ?? (() => {});

  // 1. Parse + validate in the worker.
  const parseResult = await runParsePipeline(
    input.file,
    onProgress,
    input.orchestratorOptions ?? {},
  );

  onProgress({ stage: 'map', message: 'Mapping components to Velocity equivalents…' });

  // 2. Map components.
  const matrix = getBundledMatrix();
  // Browser pipeline does not perform server-side secured-input
  // detection — public-access probes (Phase 5 / §7.8) run in the
  // worker and feed the report as warnings rather than mutating
  // mapping compatibility.
  const mappingResult = mapConfiguration(
    parseResult.config,
    matrix,
    input.targetPlatform,
    input.migrationStrategy,
    new Set<string>(),
  );

  onProgress({ stage: 'generate', message: 'Generating Velocity configuration…' });

  // 3. Generate the Velocity JSON.
  const generationResult = generateVelocityJson(
    parseResult.config,
    input.targetPlatform,
    input.migrationStrategy,
    new Set<string>(),
    matrix,
    { flattenNestedSchema: input.flattenNestedSchema },
  );

  const velocityJsonText = generationResult.configuration
    ? serializeConfiguration(generationResult.configuration)
    : '';
  const hasVelocityJson = generationResult.configuration !== null;

  // 4. Build the rich migration document (browser-safe) and adapt
  //    into the shared render model that crosses the wire to
  //    /api/render. Diagrams are dropped during model adaptation —
  //    HTML uses the document directly to retain Mermaid syntax.
  onProgress({ stage: 'render-model', message: 'Building report model…' });

  const diagrams = renderConfiguration(parseResult.config);
  // `buildMigrationDocument` requires a non-null VelocityConfiguration.
  // When generation produced none, fall back to an empty shell so the
  // narrative still renders (matches legacy /api/migrate behaviour).
  const velocityConfigForDoc: VelocityConfiguration = generationResult.configuration ?? {
    orgId: '0123456789ABCDEF',
    timestamp: Date.now(),
    feeds: [],
    realtime: [],
  };

  const migrationDocument = buildMigrationDocument(
    parseResult.config,
    mappingResult,
    diagrams,
    velocityConfigForDoc,
    input.targetPlatform,
    new Set<string>(),
    generationResult.warnings,
    input.migrationStrategy,
    { sourceFileName: parseResult.sourceFilenameDisplay },
  );

  // Append the URL Verification Checks (a.k.a. public-access checks)
  // section to the document so it appears in both the downloadable
  // HTML guide (which renders `doc.sections`) and the in-page report
  // (which is adapted from the document via
  // `buildRenderModelFromDocument`). When the user disables probes on
  // Step 2, every result is `probe-disabled` and the section copy
  // shifts to make the operator opt-out explicit.
  const probesEnabled = input.orchestratorOptions?.probesEnabled !== false;
  const documentWithProbes = attachProbeResultsToDocument(
    migrationDocument,
    parseResult.probeResults,
    { probesEnabled },
  );

  const summary = summarize(mappingResult);
  const warningRows = buildWarningRows({
    geoEventConfig: parseResult.config,
    mappingResult,
    velocityConfig: generationResult.configuration,
    targetPlatform: input.targetPlatform,
    migrationStrategy: input.migrationStrategy,
    sourceFilenameDisplay: parseResult.sourceFilenameDisplay,
    warnings: generationResult.warnings,
    errors: generationResult.errors,
    probeResults: parseResult.probeResults,
  });

  const candidateModel = buildRenderModelFromDocument(documentWithProbes, {
    summary,
    migrationStrategy: input.migrationStrategy,
    warnings: warningRows,
  });
  const parsedModel = migrationReportModelSchema.safeParse(candidateModel);
  if (!parsedModel.success) {
    const issues = parsedModel.error.issues
      .slice(0, 5)
      .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
      .join('; ');
    throw new Error(`runMigrationPipeline: render model failed schema validation: ${issues}`);
  }
  const renderModel = parsedModel.data;

  return {
    config: parseResult.config,
    validation: parseResult.validation,
    mappingResult,
    velocityConfig: generationResult.configuration,
    velocityJsonText,
    hasVelocityJson,
    renderModel,
    migrationDocument: documentWithProbes,
    probeResults: parseResult.probeResults,
    sourceFilenameDisplay: parseResult.sourceFilenameDisplay,
    warnings: generationResult.warnings,
    errors: generationResult.errors,
  };
}
